// Eligibility wizard — scoring logic for /visa-wizard and /en/visa-wizard.
// Phase P-VISA-WIZARD (2026-06). Built on top of VISA_MATRIX.
import { VISA_MATRIX, type VisaKey, type VisaRow } from "./visa-matrix";

export type Locale = "th" | "en";

export interface WizardAnswers {
  purpose: "tourism" | "remote-work" | "thai-employee" | "retire" | "marry-thai" | "study" | "hnwi" | "family-dep" | "";
  stay: "<60" | "60-180" | "180-365" | "multi-year" | "";
  work: "none" | "remote-non-thai" | "thai-employer" | "own-business" | "";
  age50: "yes" | "no" | "";
  finance: "low" | "mid" | "high" | "hnwi" | "";
}

export const EMPTY_ANSWERS: WizardAnswers = {
  purpose: "",
  stay: "",
  work: "",
  age50: "",
  finance: "",
};

export interface WizardQuestion {
  id: keyof WizardAnswers;
  questionTh: string;
  questionEn: string;
  options: { value: string; labelTh: string; labelEn: string; hintTh?: string; hintEn?: string }[];
}

export const WIZARD_QUESTIONS: WizardQuestion[] = [
  {
    id: "purpose",
    questionTh: "เป้าหมายหลักในการอยู่ประเทศไทยคืออะไร?",
    questionEn: "What is your primary purpose for staying in Thailand?",
    options: [
      { value: "tourism", labelTh: "ท่องเที่ยว / เยี่ยมญาติ", labelEn: "Tourism / visiting family" },
      { value: "remote-work", labelTh: "ทำงานออนไลน์ให้บริษัทต่างประเทศ (Digital Nomad)", labelEn: "Remote work for non-Thai employer (digital nomad)" },
      { value: "thai-employee", labelTh: "ทำงานเป็นลูกจ้างบริษัทไทย", labelEn: "Employed by a Thai company" },
      { value: "retire", labelTh: "เกษียณอายุในประเทศไทย", labelEn: "Retire in Thailand" },
      { value: "marry-thai", labelTh: "สมรสกับคนไทย", labelEn: "Married to a Thai national" },
      { value: "study", labelTh: "เรียน (ภาษา/มหาวิทยาลัย/Muay Thai)", labelEn: "Study (language/university/Muay Thai)" },
      { value: "hnwi", labelTh: "นักลงทุน / HNWI / ผู้บริหารระดับสูง", labelEn: "Investor / HNWI / Senior executive" },
      { value: "family-dep", labelTh: "ติดตามครอบครัว (Dependent)", labelEn: "Dependent of a primary visa holder" },
    ],
  },
  {
    id: "stay",
    questionTh: "ต้องการอยู่ไทยนานแค่ไหนต่อครั้ง?",
    questionEn: "How long do you plan to stay per visit?",
    options: [
      { value: "<60", labelTh: "น้อยกว่า 60 วัน", labelEn: "Less than 60 days" },
      { value: "60-180", labelTh: "60 – 180 วัน", labelEn: "60 – 180 days" },
      { value: "180-365", labelTh: "180 – 365 วัน", labelEn: "180 – 365 days" },
      { value: "multi-year", labelTh: "หลายปีต่อเนื่อง", labelEn: "Multiple years continuously" },
    ],
  },
  {
    id: "work",
    questionTh: "แผนการทำงานในไทย?",
    questionEn: "Work plans while in Thailand?",
    options: [
      { value: "none", labelTh: "ไม่ทำงาน", labelEn: "No work" },
      { value: "remote-non-thai", labelTh: "ทำงานออนไลน์ให้บริษัทต่างประเทศ", labelEn: "Remote work for non-Thai employer" },
      { value: "thai-employer", labelTh: "ทำงานให้บริษัทในไทย", labelEn: "Employed by a Thai company" },
      { value: "own-business", labelTh: "ตั้งบริษัทของตัวเอง", labelEn: "Set up my own company" },
    ],
  },
  {
    id: "age50",
    questionTh: "อายุครบ 50 ปีหรือไม่?",
    questionEn: "Are you aged 50 or above?",
    options: [
      { value: "yes", labelTh: "ใช่ 50+", labelEn: "Yes, 50+" },
      { value: "no", labelTh: "ไม่ใช่", labelEn: "No" },
    ],
  },
  {
    id: "finance",
    questionTh: "หลักฐานการเงิน / รายได้ของคุณ?",
    questionEn: "Your financial standing?",
    options: [
      { value: "low", labelTh: "ออมเงิน < 500,000 บาท / รายได้ < USD 40K/ปี", labelEn: "Savings < THB 500K / income < USD 40K/yr" },
      { value: "mid", labelTh: "ออมเงิน 500K – 1M บาท / รายได้ USD 40-80K/ปี", labelEn: "Savings THB 500K – 1M / income USD 40-80K/yr" },
      { value: "high", labelTh: "ออมเงิน > 1M บาท / รายได้ USD 80K+/ปี", labelEn: "Savings > THB 1M / income USD 80K+/yr" },
      { value: "hnwi", labelTh: "สินทรัพย์ ≥ USD 1M", labelEn: "Assets ≥ USD 1M" },
    ],
  },
];

export interface ScoredVisa {
  visa: VisaRow;
  score: number;
  reasonsTh: string[];
  reasonsEn: string[];
}

// Scoring: each visa starts at 0 and earns points based on answer fit.
// Penalties applied when an answer disqualifies/contradicts a visa.
export function scoreVisas(a: WizardAnswers): ScoredVisa[] {
  const scores: Record<VisaKey, { score: number; rTh: string[]; rEn: string[] }> = {
    tourist: { score: 0, rTh: [], rEn: [] },
    dtv: { score: 0, rTh: [], rEn: [] },
    ltr: { score: 0, rTh: [], rEn: [] },
    "non-o-retirement": { score: 0, rTh: [], rEn: [] },
    "non-o-marriage": { score: 0, rTh: [], rEn: [] },
    "non-b-work": { score: 0, rTh: [], rEn: [] },
    ed: { score: 0, rTh: [], rEn: [] },
  };

  const bump = (k: VisaKey, n: number, th: string, en: string) => {
    scores[k].score += n;
    if (n > 0) {
      scores[k].rTh.push(th);
      scores[k].rEn.push(en);
    }
  };

  // Purpose
  switch (a.purpose) {
    case "tourism":
      bump("tourist", 5, "ตรงกับการท่องเที่ยวระยะสั้น", "Matches short tourism stays");
      break;
    case "remote-work":
      bump("dtv", 5, "DTV ออกแบบมาเพื่อ Digital Nomad", "DTV is designed for digital nomads");
      bump("ltr", 3, "LTR WFTP รองรับ remote professional ระดับสูง", "LTR WFTP supports senior remote pros");
      break;
    case "thai-employee":
      bump("non-b-work", 6, "Non-B + Work Permit เป็นมาตรฐานของลูกจ้างบริษัทไทย", "Non-B + Work Permit is the standard for Thai-employer staff");
      bump("ltr", 2, "LTR HSP ลด PIT เหลือ 17%", "LTR HSP reduces PIT to 17%");
      break;
    case "retire":
      bump("non-o-retirement", 6, "Non-O Retirement สำหรับวัยเกษียณ", "Non-O Retirement is built for retirees");
      bump("ltr", 3, "LTR Wealthy Pensioner ให้ 10 ปี", "LTR Wealthy Pensioner grants 10 years");
      break;
    case "marry-thai":
      bump("non-o-marriage", 6, "Non-O Marriage เหมาะกับผู้สมรสคนไทย", "Non-O Marriage suits foreign spouses of Thai nationals");
      break;
    case "study":
      bump("ed", 6, "Non-ED ใช้กับหลักสูตรที่ MOE รับรอง", "Non-ED covers MOE-accredited programs");
      bump("dtv", 2, "DTV หมวด Muay Thai/Thai Cooking", "DTV covers Muay Thai/Thai cooking courses");
      break;
    case "hnwi":
      bump("ltr", 6, "LTR Wealthy Global Citizen / HSP สำหรับ HNWI", "LTR Wealthy Global Citizen / HSP for HNWI");
      break;
    case "family-dep":
      bump("ltr", 3, "LTR Dependent ครอบครัวสูงสุด 4 คน", "LTR Dependent for up to 4 family members");
      bump("dtv", 3, "DTV Dependent สำหรับคู่สมรส + บุตร", "DTV Dependent for spouse + children");
      bump("non-o-marriage", 2, "Non-O Dependent ตามคู่สมรส", "Non-O Dependent following spouse");
      break;
  }

  // Stay duration
  switch (a.stay) {
    case "<60":
      bump("tourist", 3, "วีซ่า Tourist เหมาะกับการพัก ≤ 60 วัน", "Tourist visa fits stays ≤ 60 days");
      break;
    case "60-180":
      bump("dtv", 2, "DTV พักได้ 180 วัน/ครั้ง", "DTV allows 180 days per stay");
      bump("tourist", 1, "Tourist ต่อ +30 ได้ถึง ~90 วัน", "Tourist extendable to ~90 days");
      bump("ed", 1, "Non-ED เริ่มต้น 90 วัน", "Non-ED starts at 90 days");
      break;
    case "180-365":
      bump("non-o-retirement", 3, "Non-O Retirement พำนัก 365 วัน", "Non-O Retirement allows 365-day stay");
      bump("non-o-marriage", 3, "Non-O Marriage พำนัก 365 วัน", "Non-O Marriage allows 365-day stay");
      bump("non-b-work", 2, "Non-B ต่อ 1 ปีเมื่อมี Work Permit", "Non-B extends to 1 year with Work Permit");
      bump("dtv", 2, "DTV ต่อในไทยอีก 180 วัน รวม 360 วัน", "DTV extension brings stay to 360 days");
      bump("ed", 2, "Non-ED ต่อตามอายุหลักสูตร", "Non-ED extends per course");
      break;
    case "multi-year":
      bump("ltr", 4, "LTR ให้สิทธิ์ 10 ปี (5+5)", "LTR grants 10 years (5+5)");
      bump("dtv", 3, "DTV multiple entry 5 ปี", "DTV is multiple-entry for 5 years");
      bump("non-o-retirement", 2, "Non-O Retirement ต่อปีต่อปี", "Non-O Retirement renewable annually");
      bump("non-o-marriage", 2, "Non-O Marriage ต่อปีต่อปี", "Non-O Marriage renewable annually");
      break;
  }

  // Work
  switch (a.work) {
    case "none":
      bump("tourist", 1, "ไม่ต้องการสิทธิทำงาน", "No work right needed");
      bump("non-o-retirement", 2, "Non-O Retirement ห้ามทำงาน", "Non-O Retirement disallows work");
      bump("ed", 1, "Non-ED ห้ามทำงาน", "Non-ED disallows work");
      break;
    case "remote-non-thai":
      bump("dtv", 4, "DTV อนุญาต remote work ให้บริษัทต่างประเทศ", "DTV permits remote work for non-Thai employers");
      bump("ltr", 3, "LTR WFTP รองรับ remote employment", "LTR WFTP supports remote employment");
      // Disqualifiers
      scores.tourist.score -= 3;
      scores.ed.score -= 3;
      break;
    case "thai-employer":
      bump("non-b-work", 5, "Non-B + Work Permit จำเป็นสำหรับงานในบริษัทไทย", "Non-B + Work Permit is required for Thai employment");
      bump("ltr", 3, "LTR ได้ Digital Work Permit อัตโนมัติ", "LTR auto-grants a Digital Work Permit");
      bump("non-o-marriage", 2, "Non-O Marriage ขอ Work Permit แยกได้", "Non-O Marriage can apply for a separate Work Permit");
      scores.tourist.score -= 4;
      scores.dtv.score -= 3;
      scores["non-o-retirement"].score -= 4;
      scores.ed.score -= 4;
      break;
    case "own-business":
      bump("non-b-work", 4, "ตั้งบริษัทแล้วยื่น Non-B + Work Permit ในนามนายจ้าง", "Form a company then apply Non-B + Work Permit");
      bump("ltr", 3, "LTR สามารถถือ Work Permit + ตั้งบริษัทได้", "LTR can hold Work Permit + set up a company");
      break;
  }

  // Age
  if (a.age50 === "yes") {
    bump("non-o-retirement", 3, "อายุ 50+ ผ่านเงื่อนไข Non-O Retirement", "50+ meets Non-O Retirement age");
    bump("ltr", 2, "อายุ 50+ ผ่านเงื่อนไข LTR Wealthy Pensioner", "50+ meets LTR Wealthy Pensioner age");
  } else if (a.age50 === "no") {
    scores["non-o-retirement"].score -= 6; // hard disqualify
  }

  // Finance
  switch (a.finance) {
    case "low":
      bump("tourist", 2, "ข้อกำหนดการเงินต่ำ", "Low financial threshold");
      bump("ed", 1, "ใช้หลักฐานผู้ค้ำได้", "Sponsor proof acceptable");
      scores.dtv.score -= 2;
      scores["non-o-retirement"].score -= 3;
      scores["non-o-marriage"].score -= 2;
      scores.ltr.score -= 5;
      break;
    case "mid":
      bump("dtv", 3, "ผ่านเกณฑ์ DTV (500K บาท)", "Meets DTV THB 500K bar");
      bump("non-o-marriage", 2, "ผ่านเกณฑ์ Non-O Marriage (400K)", "Meets Non-O Marriage (THB 400K) bar");
      bump("ed", 1, "ครอบคลุมค่าเล่าเรียนได้", "Covers tuition");
      scores.ltr.score -= 3;
      break;
    case "high":
      bump("ltr", 3, "ผ่านรายได้ USD 80K สำหรับ WFTP/Pensioner", "Meets USD 80K for WFTP/Pensioner");
      bump("dtv", 2, "ผ่าน DTV สบายๆ", "Comfortably meets DTV");
      bump("non-o-retirement", 2, "ผ่าน 800K บาทของ Non-O Retirement", "Meets Non-O Retirement THB 800K");
      break;
    case "hnwi":
      bump("ltr", 5, "ผ่านเกณฑ์ LTR Wealthy Global Citizen", "Meets LTR Wealthy Global Citizen");
      bump("dtv", 1, "ผ่าน DTV เกินพอ", "Far exceeds DTV bar");
      break;
  }

  // Convert to array and sort
  const result: ScoredVisa[] = VISA_MATRIX.map((v) => ({
    visa: v,
    score: scores[v.slug].score,
    reasonsTh: scores[v.slug].rTh,
    reasonsEn: scores[v.slug].rEn,
  }));
  result.sort((a, b) => b.score - a.score);
  return result;
}
